import { Alert, Button, Stack, Text, TextInput, Title } from '@mantine/core'
import { useForm, zodResolver } from '@mantine/form'
import { getHotkeyHandler } from '@mantine/hooks'
import { IconAlertOctagon } from '@tabler/icons-react'
import { observer } from 'mobx-react-lite'
import { parse } from 'qs'
import { useMemo, useState } from 'react'
import { useSearch } from 'wouter'
import { z } from 'zod'

import { useGlobalState } from '@/hooks/useGlobalState'
import { navigateTo } from '@/utils/navigation'

// Simple email validation function
const isValidEmail = (email: string): boolean => {
  if (!email) return false
  const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/
  return emailRegex.test(email)
}

const ForgotPasswordSchema = z.object({
  email: z.string().email({ message: 'Please enter a valid email address' }),
})

export const ForgotPasswordScreen = observer(function ForgotPasswordScreen() {
  const searchParamsString = useSearch()
  const searchParams: { email?: string } = parse(searchParamsString)

  const { api } = useGlobalState()
  const [submitError, setSubmitError] = useState<string | null>(null)
  const [isLoading, setIsLoading] = useState(false)

  const form = useForm<z.infer<typeof ForgotPasswordSchema>>({
    mode: 'controlled',
    validate: zodResolver(ForgotPasswordSchema),
    initialValues: {
      email: searchParams.email ?? '',
    },
  })

  const onKeyboardEnter = useMemo(() => getHotkeyHandler([['Enter', () => void submitForm()]]), [])

  async function submitForm() {
    setSubmitError(null)

    const validations = form.validate()

    if (validations.hasErrors) {
      setSubmitError('Please enter a valid email address.')
      return
    }

    const { email } = form.getValues()

    if (!isValidEmail(email)) {
      setSubmitError('Please enter a valid email address.')
      return
    }

    try {
      setIsLoading(true)
      setSubmitError(null)

      const response = await api.forgotPassword({ email })

      if (response && typeof response === 'object' && 'detail' in response) {
        // Success case - navigate to reset password screen
        navigateTo('resetPassword', { searchParams: { email } })
      } else {
        setSubmitError('Something went wrong. Please try again.')
      }
    } catch (error: any) {
      console.error('[ForgotPassword] Error:', error)
      setSubmitError(error?.response?.data?.message ?? 'An error occurred. Please try again.')
    } finally {
      setIsLoading(false)
    }
  }

  return (
    <Stack gap="xxl" maw={768} w="100%">
      <Title order={6} ta="center" fw={500}>
        Forgot your password?
      </Title>

      <Text ta="center" c="dimmed" size="sm">
        No worries, we&apos;ll send you reset instructions.
      </Text>

      {submitError && <Alert variant="light" color="red" title={submitError} icon={<IconAlertOctagon />}></Alert>}

      <TextInput
        withAsterisk
        label="Email Address"
        placeholder="Enter your email address"
        onKeyDown={onKeyboardEnter}
        {...form.getInputProps('email')}
        autoFocus
      />

      <Stack align="center">
        <Button
          size="lg"
          onClick={() => void submitForm()}
          fullWidth
          disabled={!isValidEmail(form.getValues().email) || isLoading}
          loading={isLoading}>
          {isLoading ? 'Sending reset instructions...' : 'Send Reset Instructions'}
        </Button>

        <Button variant="subtle" color="gray" size="compact-lg" onClick={() => navigateTo('login')}>
          Back to Login
        </Button>
      </Stack>
    </Stack>
  )
})
